/**
 * One icon, by name.
 *
 * The catalogue names its icons as strings ("badgeFounder", "gameValorant"),
 * so the lookup has to happen at runtime. Anything unknown falls back to the
 * dice glyph rather than rendering an empty box - a missing icon should look
 * like a placeholder, not like a layout bug.
 */

import { PATHS } from './paths';

export type IconProps = {
  name: string;
  size?: number;
  /** Stroke width. Heavier reads better at small sizes. */
  stroke?: number;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
};

const warned = new Set<string>();

export function Icon({ name, size = 24, stroke = 1.8, className, style, title }: IconProps) {
  const body = PATHS[name] ?? PATHS.dice ?? '';
  /* The fallback keeps a typo from rendering an empty box - which also means a
     typo renders a perfectly plausible dice and nobody notices. Loud in
     development, silent in production. */
  if ((import.meta as { env?: { DEV?: boolean } }).env?.DEV && !PATHS[name] && !warned.has(name)) {
    warned.add(name);
    console.warn('[Icon] no path named "' + name + '" - showing the dice fallback');
  }
  return (
    <svg
      viewBox="0 0 24 24"
      /* So a scene can put its own glyph in the place of one of these - a
         cowboy hat where the crew icon goes, a compass where discovery
         does - without any component knowing which theme is on. */
      data-icon={name}
      width={size}
      height={size}
      className={className}
      style={style}
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      dangerouslySetInnerHTML={
        title
          ? { __html: '<title>' + escapeXml(title) + '</title>' + body }
          : { __html: body }
      }
    />
  );
}

function escapeXml(s: string): string {
  return s.replace(/[<>&]/g, (c) => (c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&amp;'));
}
